// The email SESSION — what "Emails need attention" turns the app into (busy-my-web#99).
//
// Frames 40006949:281541 → 40008242:423135. Clicking the follow-up does not navigate: the middle
// BECOMES the Email Workspace, the Ongoing list empties, the right rail becomes the "Email
// timeline" with a single "Email session" item on it, and the Briefing summarises the inbox.
//
// Every string a frame writes is HERE rather than in a template, because Fatemeh's first rule for
// this section is that the titles are the design's own words ("به تایتل ها و عناوین دقت کن") — one
// module means a wording can be checked, and cannot drift between the middle, the rail and the box
// that all say the same thing at once.
//
// Pure by design: no store, no router, nothing mounted. The views render what these return.

import { selectedEmailCount } from '@/lib/emailWorkspace'
import { groupSize } from '@/lib/emailListPaging'
import { capitalAfterCount, countLabel, countText } from '@/lib/briefings/rows'
import { MODULE_PREFIXES } from '@/lib/searchMode'
import { suggestionRow as threadOngoingRow } from '@/lib/threadOngoingSuggestion'
import { hasThreadPicks, opensPanel, threadActions, threadCta, pickedTimelineIds } from '@/lib/emailThreadActions'
import { messageNames, participantDisplay, senderAddress, senderDisplayName } from '@/lib/senderDisplay'
// A subject FAMILY inside a group (2026-09-15 parity audit, F1-3 / bff#39) is its own kind of pick.
import { familyPicks, familySuggestions } from '@/lib/emailThreadFamilies'
// The four options a MATCHED group's selection offers, and the join that decides whether a
// selection has any (busy-my-web#422).
import { CHOOSER_TEXT, MATCH_CHOICE } from '@/lib/emailGroupMatches'
// …and what a selection of FILES is offered, and how it is counted (busy-my-web#423).
import { attachmentActions, attachmentBoardSummary, attachmentPicks, attachmentSelectionSummary } from '@/lib/emailAttachmentsListing'
// …and what an OPEN EMAIL offers to make out of itself (busy-my-web#517).
import { threadCreateOptions, threadCreateCta } from '@/lib/emailThreadCreate'

/** Verbatim design strings. Named so a change is a change to the DESIGN, not to a template. */
export const TEXT = {
  // "Email workspace" since the 2026-09-15 parity audit (busy-my-web#427, frames 40006970:297318
  // onwards): the updated section heads every stage with the workspace's own name. It was "Email
  // inbox" from the earlier frame this module was first written against.
  inboxTab: 'Email workspace',
  backTab: 'Back',
  // The selection strip's head, per KIND of pick (busy-my-web#493 / #497, frames 40011004:398018 /
  // 40008241:419693 / 40008354:240503 / 40010621:263696): "3 Emails group selected," for groups,
  // "3 Email selected," for threads, "1 Thread selected," for a subject family, and the generic
  // "N items selected," only for a selection that mixes kinds (or files, which count themselves).
  //
  // Since busy-my-web#609 (Saffet 2026-10-09, Slack p1791531260546049: "Everything listed in a
  // briefing should have a capital letter after a Number") the count carries its thousands separator
  // and the word after it its capital, and the groups head is grammatical — "1 Email group selected,"
  // / "3 Email groups selected," where the frame wrote "1 Emails group selected," (his example).
  groupsSelected: (n) => `${countLabel(n, 'Email group')} selected,`,
  threadsSelected: (n) => `${countText(n)} Email selected,`,
  familiesSelected: (n) => `${countText(n)} Thread selected,`,
  itemsSelected: (n) => `${countLabel(n, 'item')} selected,`,
  // "Add to ongoing" — the fourth option of the group-selection chooser (busy-my-web#493 (c),
  // frame 40008241:419693), and the "Add" CTA it arms (40010646:340618).
  addToOngoing: 'Add to ongoing',
  add: 'Add',
  archiveSelected: 'Archive selected groups',
  deleteSelected: 'Delete selected groups',
  // The delete's own words (acc-account-sync-srv#18, 2026-09-15 parity audit). A delete is the one
  // verb in this panel that takes mail OUT of the mailbox, so it asks first — and the receipt hands
  // it straight back, because `restoreSenders` exists and an Undo nobody has to hunt for is what
  // makes a destructive button safe to press.
  deleteCta: 'Delete',
  deleteConfirmCancel: 'Cancel',
  undo: 'Undo',
  workspaceTitle: 'Email Workspace',
  // …and what the GROUP LISTING heads its middle (frame 40008241:418476, Pouria 2026-09-15 local
  // batch): "Email groups" — the contents, not the container, the same rule the attachments
  // listing follows below.
  groupsTitle: 'Email groups',
  // …and what the ATTACHMENTS category heads its middle (busy-my-web#482, frame 40011000:380432).
  // The listing IS the answer to the Briefing's "24 attachments" row, so heading it with the
  // workspace's own name was the page naming the container instead of the contents.
  attachmentsTitle: 'Attachments',
  // Two lines in the frame (an explicit <br/>), so it is stored as two.
  workspaceSubtitle: ['Choose a category from the Briefing', 'or ask Busy.me what you would like to see'],
  groupNameColumn: 'Group name',
  // The count column's own heading. The frame (40010659-342529, measured on the 40010685:353763
  // instance) heads all four columns; the app drew this one's numbers under nothing at all
  // (busy-my-web#355). Spelled exactly as the story's connections listing already spells it
  // (lib/storyConnections.js), so one column is one word wherever a group listing is drawn.
  emailsColumn: 'Emails',
  lastUpdateColumn: 'Last update',
  timelineHeader: 'Email timeline',
  sessionTitle: 'Email session',
  sessionReady: 'Ready to organise your inbox.',
  clearSelection: 'Clear selection',
  clearSelections: 'Clear selections',
  suggestionsTitle: 'What would you like to do?',
  recommended: 'Recommended',
  boardHeading: 'Selected',
  archive: 'Archive',
  noOngoing: 'No ongoing selected',
  briefingInbox: 'Briefing Email Inbox',
  // ── Create an ongoing (busy-my-web#104, frames 40008409:184181 → 40007198:182698) ──
  next: 'Next',
  createOngoing: 'Create ongoing',
  // "Ongoing name" — the updated frames title the panel after the ONE thing it asks for
  // (2026-09-15 parity audit F2-4, busy-my-web#498, frames 40008409:184904 / 40008457:188720); the
  // crumb chain above it already says the panel is a create step, so "Create an ongoing" here was
  // the same sentence twice.
  createPanelTitle: 'Ongoing name',
  nameLabel: 'Ongoing name:',
  namePlaceholder: 'Name this ongoing',
  // What creating the ongoing actually promises, since busy-timeline-srv#21: the mail these groups
  // have ALREADY sent is linked, and everything that arrives later is SUGGESTED — the story does not
  // silently grow because someone wrote to you again. Auto-connect (the switch on the story's
  // connections panel, off by default) is what restores the old sentence, which is why both are kept
  // here rather than one being deleted.
  linkHelper: 'Emails these groups send later will be suggested for this Ongoing, so you choose what belongs.',
  linkHelperAuto: 'Every future email from these groups will automatically be linked to this Ongoing.',
  futureJoin: 'Future emails will be suggested',
  futureAppear: 'Future emails from these groups will be suggested here...',
  futureAppearAuto: 'Future emails from these groups will automatically appear here...',
  briefingCreate: 'Briefing create ongoing',
  createFailed: 'Could not create the ongoing. Try again.',
}

/** The header's item-tab: the inbox at the top level, "Back" once a group is open. */
export function headerTab(openGroup) {
  return openGroup
    ? { label: TEXT.backTab, icon: 'arrow-back', back: true }
    : { label: TEXT.inboxTab, icon: 'direct-inbox', back: false }
}

/** The story title over the middle: "Email Workspace", or "Linkedin email group" inside one. */
export function workspaceTitle(openGroup) {
  const name = groupName(openGroup)
  return name ? `${name} email group` : TEXT.groupsTitle
}

/**
 * A group's display name — the friendly name where the provider gave one, else the ADDRESS.
 *
 * "else the raw sender" was the whole header (`"Gmail" <no-reply@accounts.google.com>`), which is
 * what busy-my-web#361 reported: the group is grouped BY that string, so a sender the service could
 * not name printed the brackets and all. `senderDisplayName` reads it the one way the estate reads
 * every sender — the name inside it if there is one, the address if there is not.
 */
export function groupName(group) {
  if (!group) return ''
  return String(group.name || senderDisplayName(group.sender) || group.emailAddress || '').trim()
}

/**
 * "438 emails" — the count column, and the same phrase the Briefing and the rail use for one
 * group, so a group is never "438" in one place and "438 emails" in another.
 */
export function emailsLabel(count) {
  const n = Number(count) || 0
  // "1,733 emails" — the frames group thousands (40011004:398018), and a strip that reads "1733"
  // beside a Work-board that reads "1,733" is two spellings of one number.
  return `${n.toLocaleString('en-GB')} email${n === 1 ? '' : 's'}`
}

/**
 * WHO an email row inside an open group is from (busy-my-web#492 (b), frame 40008290:153703).
 *
 * The listing's rows come from the timeline search, whose `participants` list starts with whoever
 * the index put first — and for received mail that is the READER. Taking "the first participant
 * with a name or address" therefore printed the user's own address on five of six rows, which is
 * the audit's finding 18. The reader's own address is excluded from the participants, and the
 * message header's `senders` is read next — the bare address only as a last resort.
 *
 * The reader is recognised by ADDRESS, and — where a caller knows it — by NAME too
 * (busy-my-web#568): the Briefing's thread rows arrive from the BFF as display names only
 * ("Saffet Mustafa", no address), so an address match alone let the reader's own name lead the
 * Briefing's sender column on nearly every row.
 *
 * @param thread    a timeline record (participants[], emailMessages[])
 * @param selfEmail the signed-in user's address, or '' when unknown
 * @param selfName  the signed-in user's display name, or '' when unknown
 */
export function threadSenderName(thread, selfEmail = '', selfName = '') {
  const me = String(selfEmail || '').trim().toLowerCase()
  const myName = String(selfName || '').trim().toLowerCase()
  const isMe = (p) => (!!me && String(p?.email || '').trim().toLowerCase() === me)
    || (!!myName && senderDisplayName(p?.name || '').trim().toLowerCase() === myName)
  const others = (thread?.participants || []).filter(p => p && (p.name || p.email) && !isMe(p))
  const fromHeader = (thread?.emailMessages || []).map(m => m?.senders || '').find(s => s && !(me && String(s).toLowerCase().includes(me)))
  // A bare `From:` address is not the end of it (busy-my-web#611): the same address may be named on
  // another of the thread's headers, or on its participant record — the name Gmail prints, found.
  const heard = messageNames(thread?.emailMessages)
  if (fromHeader) {
    const address = senderAddress(fromHeader).toLowerCase()
    const named = senderDisplayName(fromHeader)
    if (named.toLowerCase() !== address) return named
    const same = others.find(p => String(p?.email || '').trim().toLowerCase() === address)
    return (same ? participantDisplay(same, null, heard) : heard.get(address)) || named
  }
  if (others.length) return participantDisplay(others[0], null, heard)
  return ''
}

/**
 * The "Last update" column: a time for today ("11:35"), a day and month for anything older
 * ("7 Jun") — exactly the two shapes the frame's 14 rows use.
 *
 * `now` is injectable so this can be tested without owning the clock.
 */
export function lastUpdate(value, now = new Date()) {
  if (!value) return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const sameDay = d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate()
  if (sameDay) return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(d)
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(d)
}

/**
 * One row of the group listing, ready to render. Its "N emails" is the number of THREADS opening the
 * group lists (`groupSize`, Saffet 2026-10-28) — the Briefing's own word for a conversation, as on
 * "N Emails" — so the row and the list behind it say the same number. `total` stays the group's
 * message count: it is what an archive or delete of the group acts on.
 */
export function groupRow(group, now = new Date()) {
  return {
    sender: group?.sender || '',
    name: groupName(group),
    emails: emailsLabel(groupSize(group)),
    total: Number(group?.totalCount) || 0,
    updated: lastUpdate(group?.lastReceivedUtc ?? group?.lastReceived ?? null, now),
  }
}

/** The whole listing, in the order the server ranked it (largest first). */
export function groupRows(groups, now = new Date()) {
  return (groups || []).filter(Boolean).map(g => groupRow(g, now))
}

// ── What is selected ────────────────────────────────────────────────────────────────────────────
// Selection lives on the Work-board (Fatemeh: "هر چیزی که انتخاب میشه حتما وارد work-board و تایم
// لاین میشه"), so these take the board's items rather than a private list — one truth, three
// surfaces reading it.

/** The board id for a picked group / email, so ticking twice is idempotent across surfaces. */
export function groupPickId(sender) { return `email-group:${sender}` }
export function emailPickId(id) { return `email:${id}` }

/** A ticked group, as it goes onto the Work-board. */
export function groupPick(group) {
  const name = groupName(group)
  return {
    id: groupPickId(group?.sender || name),
    kind: 'email-group',
    sender: group?.sender || '',
    title: name,
    count: Number(group?.totalCount) || 0,
  }
}

/**
 * A ticked email inside an open group.
 *
 * The pick's own shape is unchanged, and `emailId` is the fact that makes it resolvable: the rows
 * come from the timeline listing, so it is a THREAD id — and a thread is what the estate refers to
 * an email by (ObjectType has no Email member; the conversation is the object). The canonical
 * reference is stamped on at the board boundary, by `objectRefOf` in lib/objectRef.js
 * (busy-timeline-srv#20), rather than here: the board is where every surface's items meet, so one
 * place derives it for all of them and this stays the pure shape three surfaces compare against.
 */
export function emailPick(email, group) {
  return {
    id: emailPickId(email?.id),
    kind: 'email-pick',
    emailId: email?.id,
    title: groupName(group) || email?.name || '',
    subject: email?.subject || '',
    count: 1,
  }
}

/** Only the email-session picks — the board holds research items too. */
export function sessionPicks(items) {
  return (items || []).filter(i => i && (i.kind === 'email-group' || i.kind === 'email-pick' || i.kind === 'email-family'))
}

/** How many emails the picks stand for — the number every "N Emails" line quotes. */
export function pickedEmailCount(items) {
  return sessionPicks(items).reduce((sum, i) => sum + (Number(i.count) || 0), 0)
}

/**
 * The Briefing's selection summary — two spans, as the frame writes them:
 * "3 items selected," then "733 emails". Null when nothing is ticked, so the line is absent
 * rather than reading "0 items selected,".
 */
export function selectionSummary(items) {
  const picks = sessionPicks(items)
  // A selection of FILES counts items and nothing else (busy-my-web#423, frame 40011000:380706
  // writes "2 items selected" with no email total after it): a file is not an email, and
  // `attachmentPick` deliberately carries `count: 0` so five files can never read as five emails.
  if (!picks.length) {
    const files = attachmentSelectionSummary(items)
    return files ? { items: files.text, emails: '', files: files.rows } : null
  }
  // The head names the KIND that was picked (busy-my-web#493/#497): the frames never write "items"
  // over a selection of groups — "3 Emails group selected, 733 emails" — nor over threads. Only a
  // selection mixing kinds keeps the generic word, because then no one noun is true of all of it.
  const kinds = new Set(picks.map(p => p.kind))
  const n = picks.length
  const head = kinds.size > 1 ? TEXT.itemsSelected(n)
    : kinds.has('email-group') ? TEXT.groupsSelected(n)
      : kinds.has('email-family') ? TEXT.familiesSelected(n)
        : TEXT.threadsSelected(n)
  return {
    items: head,
    // "694 Emails" — a Briefing line, so capitalised after the count (busy-my-web#609); `emailsLabel`
    // itself stays lower case for the sentences that quote it ("Archive LinkedIn 694 emails").
    emails: capitalAfterCount(emailsLabel(pickedEmailCount(items))),
    rows: selectionStripRows(items),
  }
}

/**
 * The rows under the selection line (busy-my-web#493 (a), frames 40011004:398018 / 40008241:419693
 * / 40008354:240503): every pick, its count and a "View ↗" — a 3-column grid in the strip, so
 * the fourteen groups of a select-all are readable without the Work-board.
 *
 * `view` says what the link opens: a GROUP opens its listing, a thread opens the conversation, a
 * family opens the families listing on that subject.
 */
export function selectionStripRows(items) {
  return sessionPicks(items).map(p => ({
    id: p.id,
    kind: p.kind,
    icon: p.kind === 'email-group' ? 'sms-tracking' : 'sms',
    name: p.kind === 'email-pick' ? (p.subject || p.title || '') : (p.title || ''),
    count: Number(p.count) || 0,
    view: p.kind === 'email-group'
      ? { kind: 'group', sender: p.sender || p.title }
      : p.kind === 'email-pick'
        ? { kind: 'thread', id: p.emailId, subject: p.subject || '' }
        : { kind: 'family', key: p.key },
  }))
}

/** The Work-board's own heading pair: "Selected" over "733 Emails" (capital E, per the frame). */
export function boardSummary(items) {
  const total = pickedEmailCount(items)
  // …and "Selected · 2 Attachments" for a selection of files (busy-my-web#482, frame
  // 40011000:380706). Checked first: `pickedEmailCount` is 0 for every file pick on purpose
  // (`attachmentPick` carries `count: 0`), so without this the pane simply did not draw.
  if (!total) {
    const files = attachmentBoardSummary(items)
    return files ? { heading: files.heading, count: files.count } : null
  }
  return { heading: TEXT.boardHeading, count: countLabel(total, 'Email') }
}

/** The rail's selection stack: one row per pick — icon card + name + "438 emails".
 *
 * FILES are in it too (busy-my-web#482, 2026-09-15 parity audit D-6; frame 40011000:380706 draws
 * "Email session / 2 items selected" with the two file cards under it and "Clear selection"
 * beneath). They were not, because this read `sessionPicks`, which is groups and threads by
 * definition — so a selection of attachments left the rail saying "Ready to organise your inbox"
 * while the middle and the board both showed it. A file row has no "438 emails" under it: the
 * second line is its own kind, or nothing.
 */
export function selectionRows(items) {
  const emails = sessionPicks(items).map(i => ({
    id: i.id,
    icon: i.kind === 'email-group' ? 'sms-tracking' : 'sms',
    name: i.title,
    // A family stands for N emails (frame 40010621:263696's rail: "Linkedin / 12 emails").
    detail: i.kind === 'email-pick' ? (i.subject || emailsLabel(1)) : emailsLabel(i.count),
  }))
  const files = attachmentPicks(items).map(i => ({
    id: i.id,
    icon: isImagePick(i) ? 'gallery' : 'file-doc',
    name: i.title,
    detail: '',
  }))
  return [...emails, ...files]
}

/** A picked file that is a PICTURE — the one thing the rail's card glyph turns on. */
function isImagePick(pick) {
  const name = String(pick?.title || '')
  return /\.(png|jpe?g|gif|webp|bmp|heic|heif|svg|tiff?)$/i.test(name)
}

/**
 * The "Email session" timeline item — the ONE item the rail carries at rest, and the running
 * description of what is in hand:
 *   nothing ticked   → "Ready to organise your inbox."   (frames 2, 3, 7)
 *   groups ticked    → "1 group selected"                (frame 8)
 *   emails ticked    → "3 items selected"                (frame 6)
 */
export function sessionItem(items) {
  const picks = sessionPicks(items)
  // A selection of FILES is a selection (busy-my-web#482): the rail says "N items selected" for it
  // exactly as it does for threads, rather than reporting an idle session over a full Work-board.
  if (!picks.length) {
    const files = attachmentPicks(items)
    if (files.length) {
      return { title: TEXT.sessionTitle, description: `${files.length} item${files.length === 1 ? '' : 's'} selected`, emails: '' }
    }
    return { title: TEXT.sessionTitle, description: TEXT.sessionReady, emails: '' }
  }
  const groups = picks.filter(p => p.kind === 'email-group')
  const description = groups.length === picks.length
    ? `${groups.length} group${groups.length === 1 ? '' : 's'} selected`
    : `${picks.length} item${picks.length === 1 ? '' : 's'} selected`
  // …and the "733 emails" total under it (busy-my-web#497, frames 40008241:419693 /
  // 40010687:355542): the rail says how much mail is in hand, not only how many rows.
  return { title: TEXT.sessionTitle, description, emails: emailsLabel(pickedEmailCount(items)) }
}

// ── The suggestion panel ────────────────────────────────────────────────────────────────────────
// "What would you like to do?" — the options the frames offer, in their order, with Recommended on
// the first. Which options exist depends only on WHERE you are and WHAT is ticked, so it is a
// function of those two things and nothing else.
//
// `ready` says whether the option can actually be carried out today. The archive is real (the BFF's
// `email/archiveSenders`, busy-main-bff#7) and so, since #104, is "Create ongoing"
// (`ongoing/from-email-groups`); a bulk delete by sender still has no endpoint at all, so it alone
// is shown — the frame writes it — and marked, rather than being quietly wired to nothing.
export const GROUPS_UNDER = 5

/** The group-SELECTION's own "Add to ongoing" (busy-my-web#493 (c)) — one id, wherever it is armed from. */
export const SELECTION_CHOICE = { addToOngoing: 'add-to-ongoing' }

/**
 * Which armed options ARM the "Add" CTA that opens the "Possible Ongoing matches" panel
 * (2026-09-15 parity audit F1-4, frames 40010646:340618 → 40010646:341095): the group
 * selection's, the open group's, a thread selection's and a family's. One list, so the CTA, the
 * store's commit and the panel agree about which doors lead here.
 */
export function addsOnCommit(id) {
  return id === SELECTION_CHOICE.addToOngoing
    || id === MATCH_CHOICE.addToOngoing
    || id === 'threads-add-ongoing'
    || id === 'family-add-ongoing'
}

// `matches` is still accepted (every caller passes the cards) but no longer changes the answer —
// see the note in the selection branch below (Pouria, 2026-09-18).
// eslint-disable-next-line no-unused-vars
export function suggestions({ openGroup = null, items = [], groups = [], threadSuggestion = null, unread = null, matches = [], familiesOpen = false, openThread = null, listing = false } = {}) {
  const picks = sessionPicks(items)
  // "These 3 threads look like one piece of work: Insurance Renewal" (busy-my-web#275). It leads the
  // list wherever it applies — inside an open group or out at the listing — because it is the one
  // option that already knows what the selection IS, and it takes Recommended off the others for the
  // same reason. Null (no offer, or too weak an offer) leaves every existing list untouched.
  const threadRow = threadOngoingRow(threadSuggestion)
  const lead = rows => (threadRow ? [threadRow, ...rows.map(r => ({ ...r, recommended: false }))] : rows)

  // THREADS first (busy-my-web#274). A ticked email used to fall through to the group branch below
  // and be offered "Archive selected groups" / "Delete selected groups" / "Create ongoing" — two
  // sentences about the wrong noun, and a create that could not run at all, because creating from
  // picks follows SENDERS and a thread pick carries none. A selection holding any thread gets the
  // thread row instead — INCLUDING inside an open group, which is where individual emails are
  // ticked in the first place: the open group is where you are, the ticks are what you chose, and
  // the verbs belong to what you chose. A selection of only sender groups is unchanged.
  // …and where there IS an offer (busy-my-web#275) it leads them, and it stands IN PLACE of the two
  // generic ongoing verbs: "Create Ongoing" and "Add to existing Ongoing" are precisely the two
  // things the offer already proposes, with a name and a story attached, and the panel must not ask
  // the same question twice in one list. Everything else the threads can have done to them —
  // grouped, summarised, turned into a task, archived, marked, deleted, exported — is untouched.
  if (hasThreadPicks(items)) {
    const verbs = threadActions({ items, unread, listing })
    return lead(threadRow
      ? verbs.filter(v => v.id !== 'threads-create-ongoing' && v.id !== 'threads-add-ongoing')
      : verbs)
  }
  // FILES (busy-my-web#423, frames 40011000:380650 / :380706). Checked after threads and before
  // everything else for the same reason the thread branch is checked first: the verbs belong to what
  // was CHOSEN, and a selection of attachments has nothing to do with archiving senders. A selection
  // holding both a thread and a file keeps the thread verbs, which are the ones that can act on
  // both — a file's own four options are about files alone.
  if (attachmentPicks(items).length) return lead(attachmentActions(items))
  // A subject FAMILY ticked inside a group's "N Threads" listing (2026-09-15 parity audit F1-3,
  // frame 40010621:263696): "Archive thread / Delete thread / Create ongoing / Add to ongoing".
  // Before the thread branch above would have caught it, because a family is not an `email-pick`
  // — it is one row standing for twelve conversations.
  if (familyPicks(items).length) return lead(familySuggestions({ items }))
  // AN EMAIL OPEN IN THE MIDDLE (busy-my-web#517, frame 40011598:356726): with nothing ticked the
  // question is about the mail being read — "Add to Tasks (Recommended) / Add to Notes / Create
  // Event". Checked after every selection branch because a tick is a choice the reader made on top
  // of reading, and before the open group's chooser because the email is where the reader IS.
  if (openThread) return threadCreateOptions()
  if (openGroup) {
    const name = groupName(openGroup)
    const count = Number(openGroup.totalCount) || 0
    // …and the families listing's own at-rest chooser (frame 40010619:262299): the same four
    // verbs over the group's THREADS rather than over the group.
    if (familiesOpen) {
      return lead([
        { id: 'archive-group', text: `Archive ${name} Threads`, recommended: true, ready: true },
        // READY since acc-account-sync-srv#18 (2026-09-15 parity audit): `POST v1/email/deleteSenders`
      // is the endpoint this option was drawn greyed for want of.
      { id: 'delete-group', text: `Delete ${name} Threads`, recommended: false, ready: true },
        { id: 'create-ongoing', text: TEXT.createOngoing, recommended: false, ready: true },
        { id: MATCH_CHOICE.addToOngoing, text: TEXT.addToOngoing, recommended: false, ready: true },
      ])
    }
    return lead([
      { id: 'archive-group', text: `Archive ${name} ${emailsLabel(count)}`, recommended: true, ready: true },
      { id: 'delete-group', text: `Delete ${name} ${emailsLabel(count)}`, recommended: false, ready: true },
      { id: 'create-ongoing', text: TEXT.createOngoing, recommended: false, ready: true },
      // The fourth option the updated section adds (busy-my-web#422, frame 40010799:360028): file
      // this whole group into a story that already exists. Since the 2026-09-15 parity audit (F1-4)
      // it ARMS an "Add" CTA that opens the "Possible Ongoing matches" panel, rather than swapping
      // the middle for the picker at once — the picker is that panel's Search step.
      { id: MATCH_CHOICE.addToOngoing, text: CHOOSER_TEXT.addToOngoing, recommended: false, ready: true },
    ])
  }
  // An EXPLICIT selection is always actionable (busy-my-web#128). Whatever is ticked — five groups
  // or fifty — the user asked for those, so the verbs for them are offered without any count rule:
  // the under-5 gate below belongs to the UNPROMPTED clean-up offer alone, which is the app
  // proposing a bulk archive nobody asked for. Hence this branch returns before it.
  if (picks.length) {
    // …with the counts the create-flow frame writes on them (busy-my-web#497, frame
    // 40008409:183245: "Archive selected groups 26 emails"). Delete stays drawn and unready — a
    // bulk delete by sender still has no endpoint (2026-09-15 parity audit, flagged on #497).
    const total = emailsLabel(pickedEmailCount(items))
    const generic = [
      { id: 'archive-selected', text: `${TEXT.archiveSelected} ${total}`, recommended: true, ready: true },
      { id: 'delete-selected', text: `${TEXT.deleteSelected} ${total}`, recommended: false, ready: true },
      { id: 'create-ongoing', text: TEXT.createOngoing, recommended: false, ready: true },
      // The fourth verb the frames give a group selection (busy-my-web#493 (c), 40008241:419693 /
      // 40011004:398018): file the ticked groups into a story that already exists. It arms the
      // "Add" CTA → "Possible Ongoing matches" panel (F1-4), the same door the open group's own
      // "Add to Ongoing" and a thread's "Add to existing Ongoing" now go through.
      { id: SELECTION_CHOICE.addToOngoing, text: TEXT.addToOngoing, recommended: false, ready: true },
    ]
    // NO "Connect to suggested Ongoing" / "Dismiss suggested match" in front of them any more
    // (Pouria, 2026-09-18, screenshot of the six-row chooser: "it's not like the designs, we don't
    // need the first two"). busy-my-web#422 / #493 (b) used to prepend the match's two answers when
    // a ticked group had an offer standing against it; the selection chooser now asks the same
    // four verbs whatever is ticked, Recommended on Archive. The offer itself is still answered
    // where its card is drawn — the match cards in the middle (#317) and the "Possible Ongoing
    // matches" panel behind "Add to ongoing" — and the store's `connectSuggestedGroups` /
    // `dismissSuggestedGroups` legs stay for those doors.
    return lead(generic)
  }
  // Nothing ticked: the frame's standing offer, and it is DATA — "Clean-up 5 groups under 5
  // emails" only means anything when there are small groups to clean up.
  //
  // busy-my-web#126: bulk-archiving a large, unseen set is never a sane offer — Saffet's report
  // was a "Clean up 17 email groups" suggestion nobody would act on blindly. So the suggestion is
  // withheld once it would cover GROUPS_UNDER (5) or more groups; below that the count in the
  // wording is small enough a person can picture what they are archiving.
  const small = (groups || []).filter(g => g && (Number(g.totalCount) || 0) < GROUPS_UNDER)
  if (!small.length || small.length >= GROUPS_UNDER) return []
  return [{
    id: 'cleanup',
    text: `Clean-up ${small.length} group${small.length === 1 ? '' : 's'} under ${GROUPS_UNDER} emails`,
    recommended: true,
    ready: true,
    senders: small.map(g => g.sender),
  }]
}

/**
 * Whether the archive banner still owns the panel (busy-my-web#128).
 *
 * Frame 12 replaces "What would you like to do?" with the receipt: the archive IS what just
 * happened, and there is nothing in hand to ask about. But the banner is only dismissed by its own
 * ×, so it outlived the moment it described — tick five groups after an archive and the receipt was
 * still holding the panel, leaving a selection with no verbs at all. That is Saffet's dead end.
 *
 * So a SETTLED receipt ("733 emails archived", or the error) steps aside the moment something is
 * ticked again: the new selection is the newer fact, and the receipt itself is not lost — it is on
 * the rail permanently (see `archiveReceipt`). An archive still RUNNING keeps the panel, because
 * nothing new can be committed until it lands.
 */
export function standingNotice(notice, items = []) {
  if (!notice) return null
  // …and the same for a delete or its undo while they are RUNNING (acc-account-sync-srv#18).
  if (notice.kind === 'archiving' || notice.kind === 'deleting' || notice.kind === 'restoring') return notice
  return sessionPicks(items).length ? null : notice
}

/**
 * The email session's commit controls — "Clear selections" and the primary CTA.
 *
 * The frames put BOTH in the box's action bar, beside the send control: "Clear selections" as a
 * grey text button, and the CTA in the send pill's own spot ("Archive" in 40008242:421692, "Next"
 * then "Create ongoing" in 40008409:184181 → 40007198:182698). They are described here rather than
 * in a template so the panel that renders them and the box that yields the send spot to them are
 * reading one answer (busy-my-web#129).
 *
 * While a create is RUNNING the frames drop the labelled CTA row altogether (the round send arrow
 * is back) — the thing has been committed and is on its way.
 */
export function commitControls({
  items = [],
  armed = null,
  createStage = null,
  name = '',
  targets = [],
  archiving = false,
  deleting = false,
  creating = false,
  running = false,
  addPanel = null,
  confirmingDelete = false,
  picker = null,
} = {}) {
  // ── The delete CONFIRMATION owns the action bar (Pouria, 2026-09-16) ────────────────────────
  // "on delete we need to blur the rest not the smart box, we need to show the notification and
  // inside the notification we dont have any btn like this". So the confirmation itself is a plain
  // notice — trash icon and the question, nothing to press — and the two verbs are HERE, in the
  // box's action row, where every other commit in this session is pressed. "Clear selections" is
  // dropped while the question is up: there is one decision to make, and clearing the picks
  // underneath it is not one of them.
  // `danger` is what the row draws in red: a LABEL in #ff0000, never a filled red pill
  // (busy-my-web#521 — the destructive word takes the colour, the button takes none).
  if (confirmingDelete) {
    return {
      clear: false,
      cancel: { kind: 'cancel-delete', label: TEXT.deleteConfirmCancel },
      cta: { kind: 'confirm-delete', label: TEXT.deleteCta, danger: true, disabled: !!deleting },
    }
  }
  if (creating) return { clear: false, cta: null }
  // The Ongoing PICKER owns the row while it is the middle (busy-my-web#535, frames add 8 → search
  // 4): "Search", its three dots, and a "Clear selections" that clears the SEARCH. Already decided
  // by lib/ongoingPicker's `pickerControls` (the store's `ongoingPickerControls`), so it is passed
  // through whole — the armed "Add to ongoing" underneath it has nothing left to say.
  if (picker) return picker
  // "Clear selections" stands beside the send arrow for ANY selection, files included
  // (busy-my-web#482, frame 40011000:380706 draws it under a two-file pick).
  const clear = sessionPicks(items).length > 0 || attachmentPicks(items).length > 0
  // "Add to ongoing" armed (2026-09-15 parity audit F1-4): the CTA reads "Add" and opens the
  // "Possible Ongoing matches" panel; with the panel open it commits the picked match, and once a
  // query is typed it reads "Search" and opens the page-level listing on it. `addPanel` is the
  // panel's own state (lib/ongoingMatchesPanel's `panelCta` decides the word and the enabled-ness).
  if (addsOnCommit(armed)) {
    const cta = addPanel?.open ? addPanel.cta : { label: TEXT.add, disabled: !!running }
    return { clear, cta: cta ? { kind: 'add', ...cta } : null }
  }
  // The thread row's own CTA (busy-my-web#274). Checked before the archive branch because
  // `threads-archive` is an archive too, but a bulk-by-THREAD one — `archivesOnCommit` names the
  // three sender-based options and must not be widened to cover a different endpoint.
  //
  // Options that open a panel (the ongoing chooser, the group naming step) get no action-bar button:
  // they bring their own, and two controls saying the same word is two ways to press one thing.
  // The open email's own create (busy-my-web#517, frame 40011618:358535): "Add" where the arrow was.
  const emailCreateCta = threadCreateCta(armed, { running })
  if (emailCreateCta) return { clear, cta: emailCreateCta }
  const threadLabel = opensPanel(armed) ? null : threadCta(armed, { count: pickedTimelineIds(items).length })
  if (threadLabel) {
    return { clear, cta: { kind: 'threads', label: threadLabel, disabled: !!running } }
  }
  if (archivesOnCommit(armed)) {
    return { clear, cta: { kind: 'archive', label: TEXT.archive, disabled: archiving } }
  }
  // …and the delete, which is the same gesture with a different endpoint and one extra step: the
  // button opens the confirm strip rather than deleting (acc-account-sync-srv#18).
  if (deletesOnCommit(armed)) {
    return { clear, cta: { kind: 'delete', label: TEXT.deleteCta, disabled: !!deleting } }
  }
  // Both creates take the same two-step CTA — "Next", then "Create ongoing" — because they are the
  // same gesture with a different membership: `create-ongoing` connects the picked GROUPS,
  // `thread-ongoing` holds the picked THREADS (busy-my-web#275). What each sends is decided by the
  // store, not by this button.
  if (createsOnCommit(armed)) {
    return {
      clear,
      cta: {
        kind: 'create',
        label: createCta(createStage),
        disabled: createStage === 'naming' && !canCreate(name, targets),
      },
    }
  }
  return { clear, cta: null }
}

/** Which suggestions commit a CREATE — the ones that arm the "Next" → "Create ongoing" pair. */
export function createsOnCommit(id) {
  return id === 'create-ongoing' || id === 'thread-ongoing'
}

/** Which suggestions commit an archive — the ones that arm the primary "Archive" button. */
export function archivesOnCommit(id) {
  return id === 'archive-group' || id === 'archive-selected' || id === 'cleanup'
}

/**
 * The senders an armed suggestion would archive. Kept apart from the button so what is about to be
 * sent can be asserted without pressing anything.
 */
export function archiveTargets(armed, { openGroup = null, items = [], groups = [] } = {}) {
  if (armed === 'archive-group') return openGroup?.sender ? [openGroup.sender] : []
  if (armed === 'archive-selected') {
    return sessionPicks(items).filter(i => i.kind === 'email-group').map(i => i.sender).filter(Boolean)
  }
  if (armed === 'cleanup') {
    return (groups || []).filter(g => g && (Number(g.totalCount) || 0) < GROUPS_UNDER).map(g => g.sender).filter(Boolean)
  }
  return []
}

/** Which suggestions commit a DELETE — the two the greyed-out option became (acc-account-sync-srv#18). */
export function deletesOnCommit(id) {
  return id === 'delete-group' || id === 'delete-selected'
}

/**
 * The senders an armed delete would remove — the same shape `archiveTargets` answers, because the
 * two verbs act on exactly the same thing and only differ in what the server then does with it.
 * Kept apart from the button so what is about to be sent can be asserted without pressing anything,
 * and so the CONFIRM strip and the request cannot disagree about which groups are in it.
 */
export function deleteTargets(armed, { openGroup = null, items = [] } = {}) {
  if (armed === 'delete-group') return openGroup?.sender ? [openGroup.sender] : []
  if (armed === 'delete-selected') {
    return sessionPicks(items).filter(i => i.kind === 'email-group').map(i => i.sender).filter(Boolean)
  }
  return []
}

/**
 * What the strip ASKS before anything is deleted.
 *
 * Deleting is the one verb here that takes mail out of the mailbox, so it is never the direct
 * consequence of a single press: the CTA opens this, and this has its own Delete. The question names
 * the real numbers — how many emails, from how many groups — because "Are you sure?" over an
 * unnamed quantity is a dialog people learn to click through.
 *
 * Null when there is nothing to ask about, which is what keeps the strip off the screen.
 */
export function deleteConfirmation(senders = [], groups = []) {
  const picked = new Set((senders || []).filter(Boolean))
  if (!picked.size) return null
  const covered = (groups || []).filter(g => g && picked.has(g.sender))
  const emails = selectedEmailCount(groups, picked)
  const groupWord = `${picked.size} group${picked.size === 1 ? '' : 's'}`
  return {
    senders: [...picked],
    emails,
    groups: covered.length,
    // The count is the SERVER's business until it answers; here it is what the listing says it has,
    // and a group whose count never loaded contributes 0 rather than a guess.
    text: emails > 0
      ? `Delete ${emails} email${emails === 1 ? '' : 's'} from ${groupWord}?`
      : `Delete ${groupWord}?`,
    confirm: TEXT.deleteCta,
    cancel: TEXT.deleteConfirmCancel,
  }
}

/**
 * What the rail keeps after a delete — the archive receipt's own shape, so the timeline draws both
 * the same way, plus the one thing an archive receipt has no need of: `undo`, carrying the senders
 * that were deleted so `restoreSenders` can be called with exactly the body that deleted them.
 */
export function deleteReceipt({ groups = [], senders = [], result = null, at = new Date(), category = '' } = {}) {
  const picked = new Set(senders || [])
  const covered = (groups || []).filter(g => g && picked.has(g.sender))
  const requested = selectedEmailCount(groups, picked)
  const deleted = Number.isFinite(Number(result?.deleted)) && result?.deleted != null
    ? Number(result.deleted)
    : requested
  const when = at instanceof Date ? at : new Date(at)
  return {
    id: `email-delete:${when.getTime()}`,
    icon: 'trash',
    title: `${deleted} email${deleted === 1 ? '' : 's'} deleted`,
    description: String(category || '').trim() || TEXT.workspaceTitle,
    date: new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(when),
    time: new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: true }).format(when).toLowerCase().replace(/\s?(am|pm)/, ' $1'),
    detail: `${covered.length} group${covered.length === 1 ? '' : 's'}`,
    rows: covered.map(g => ({ id: g.sender, icon: 'sms-tracking', text: `${groupName(g)}(${Number(g.totalCount) || 0})` })),
    // Everything the Undo needs, on the receipt itself: a receipt scrolled past is still an Undo.
    undo: { label: TEXT.undo, senders: [...picked] },
  }
}

// ── Create an ongoing from the picked groups (busy-my-web#104) ──────────────────────────────────
// Frames 40008409:184181 (choose it) → 184904 (name it) → 40008457:187991 (unlink one) → 188720
// (creating) → 40007198:182698 (created). Everything below is a function of the SAME picks the
// archive reads, so what the naming step says it will link is exactly what the Work-board shows.

/**
 * The groups a create would connect: whatever is ticked, or — inside one open group — that group.
 * Email picks are not groups, so they are not connections: this feature follows SENDERS.
 */
export function createTargets({ openGroup = null, items = [], groups = [] } = {}) {
  const picks = sessionPicks(items).filter(i => i.kind === 'email-group')
  if (picks.length) {
    // The bare address is the mailbox's fact, not the board's: a board pick carries what is drawn
    // (name + count), so the address is resolved against the live groups the Briefing is holding.
    const addressOf = (sender) => (groups || []).find(g => g && g.sender === sender)?.emailAddress || ''
    return picks.map(p => ({
      sender: p.sender || p.title,
      name: p.title,
      emailAddress: addressOf(p.sender || p.title),
      count: Number(p.count) || 0,
    }))
  }
  if (openGroup) {
    return [{
      sender: openGroup.sender || groupName(openGroup),
      name: groupName(openGroup),
      emailAddress: openGroup.emailAddress || '',
      count: Number(openGroup.totalCount) || 0,
    }]
  }
  return []
}

/** How many emails those groups stand for — the "26 emails" every line of the step quotes. */
export function targetEmailCount(targets) {
  return (targets || []).reduce((sum, t) => sum + (Number(t.count) || 0), 0)
}

/** "3 email groups" / "1 email group". */
export function groupsCountLabel(n) {
  // A Briefing row (the create step's) — "3 Email groups" since busy-my-web#609.
  return countLabel(n, 'Email group')
}

/** "26 linked emails" — the Briefing's second row while the create step is open. */
export function linkedEmailsLabel(n) {
  // "26 Linked emails" — a Briefing row, capitalised after the count (busy-my-web#609).
  return countLabel(n, 'linked email')
}

/**
 * The naming step's link-confirmation block: "Link 3 selected groups" + "26 emails", over the
 * promise underneath it. Null with nothing to link, so the block is absent rather than empty —
 * which is also what unlinking the LAST group leaves behind.
 */
export function linkConfirmation(targets) {
  const list = targets || []
  if (!list.length) return null
  return {
    text: `Link ${list.length} selected group${list.length === 1 ? '' : 's'}`,
    emails: emailsLabel(targetEmailCount(list)),
    helper: TEXT.linkHelper,
  }
}

/**
 * What the name field starts as. One group names itself — that IS what the user picked, so it is
 * true rather than invented; several groups have no honest shared name, so the field starts empty
 * and the CTA waits for one (see `canCreate`).
 */
export function suggestedOngoingName(targets) {
  const list = targets || []
  // The same naming rule the row itself drew (busy-my-web#361): the suggestion is the name the user
  // just read on the group, never the header behind it.
  return list.length === 1 ? String(list[0].name || senderDisplayName(list[0].sender) || '').trim() : ''
}

/** The CTA's label: "Next" while choosing, "Create ongoing" once naming (the frames' own change). */
export function createCta(stage) {
  return stage === 'naming' ? TEXT.createOngoing : TEXT.next
}

/** A create needs a name and at least one group — nothing else. */
export function canCreate(name, targets) {
  return String(name || '').trim().length > 0 && (targets || []).length > 0
}

/** The request body for `ongoing/from-email-groups` (busy-main-bff → busy-timeline-srv). */
export function createRequest(name, targets) {
  return {
    name: String(name || '').trim(),
    groups: (targets || []).map(t => ({
      sender: t.sender || '',
      name: t.name || '',
      emailAddress: t.emailAddress || '',
      emailCount: Number(t.count) || 0,
    })),
  }
}

/**
 * The banner that replaces the create panel (frames 8 and 9): the spinner while it runs, the green
 * receipt when it lands, an honest line when it fails. The created banner uses the ongoing's ACTUAL
 * name — the frame writes "Ongoing created,My Health Issue" over a story called "Health Issues",
 * which is a typo in the source file, not two different names (see the Dev notes).
 */
export function createNotice(status, name, ongoingId = '') {
  const label = String(name || '').trim()
  if (status === 'creating') return { kind: 'creating', text: `Creating ongoing, ${label}` }
  // …with the frame's "View" (40007198:182698, busy-my-web#498): the story the band names is one
  // click away from the band itself, not only from the redirect that already happened.
  if (status === 'created') {
    const id = String(ongoingId || '').trim()
    return { kind: 'created', text: `Ongoing created, ${label}`, view: id ? `/ongoing/${id}` : '' }
  }
  if (status === 'error') return { kind: 'error', text: TEXT.createFailed }
  return null
}

// ── The archive receipt ─────────────────────────────────────────────────────────────────────────

/**
 * What the timeline keeps after an archive (frame 12): a permanent entry with the total, the
 * groups it covered and a per-group breakdown — "Linkedin(438)".
 *
 * Built from the groups AS THEY WERE before the call, because they are gone from the list by the
 * time this is written; the total comes from the SERVER's count where it gave one, so the receipt
 * cannot claim more than was actually filed away.
 */
export function archiveReceipt({ groups = [], senders = [], result = null, at = new Date(), category = '' } = {}) {
  const picked = new Set(senders || [])
  const covered = (groups || []).filter(g => g && picked.has(g.sender))
  const requested = selectedEmailCount(groups, picked)
  const archived = Number.isFinite(Number(result?.archived)) && result?.archived != null
    ? Number(result.archived)
    : requested
  const when = at instanceof Date ? at : new Date(at)
  return {
    id: `email-archive:${when.getTime()}`,
    icon: 'archive',
    title: `${archived} email${archived === 1 ? '' : 's'} archived`,
    // "Social media email groups" where every archived group sat in one category (frame
    // 40008242:423135, 2026-09-15 parity audit F1-1 (d)); the workspace's own name otherwise.
    description: String(category || '').trim() || TEXT.workspaceTitle,
    date: new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(when),
    time: new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: true }).format(when).toLowerCase().replace(/\s?(am|pm)/, ' $1'),
    detail: `${covered.length} group${covered.length === 1 ? '' : 's'}`,
    // "Linkedin(438)" — the frame's own bracketed form.
    rows: covered.map(g => ({ id: g.sender, icon: 'sms-tracking', text: `${groupName(g)}(${Number(g.totalCount) || 0})` })),
  }
}

// ── Who owns the middle while an email session is alive (acc-account-sync-srv#11) ──────────────
//
// The session opens the workspace IN PLACE — that is the whole point of "Emails need attention"
// not being a navigation — so the shell hands the middle column to EmailWorkspace whenever the
// session is active or the Briefing has groups, wherever you happen to be. Which meant the thread
// view could not be reached: clicking a row's Open changed the URL to /emails/timeline/:id and the
// workspace stayed on screen, so the row's one affordance quietly did nothing.
//
// The thread route WINS. Three routes are exempt, for three different reasons:
//
//   • /emails/workspace — the ROUTER renders the workspace there; claiming it here as well would
//     draw it twice. (This exemption is older than the thread one.)
//   • /emails/timeline/:id — you asked for a conversation, so you get the conversation.
//   • /settings/* — THE PAGE THAT HAS NO BOX (busy-my-web#562, Fatemeh 2026-09-22: "i clicked
//     email need attention, then clicked subscription. after that I clicked on my profile to
//     manage something, suddenly i lost everthing. no icon to back, no icon to close and i have to
//     refresh the website"). The avatar's "Manage your account" pushes /settings/account, and the
//     shell deliberately HIDES the universal input box on every settings route but Integrations
//     (Content.vue's `showSmartBox`, they are full-height pages). With the session still alive this
//     rule went on claiming the middle there, so the settings page never appeared and the workspace
//     was drawn in a shell that draws no box — and "← Back" / "✕ Close" live in the box's gutter
//     (lib/boxChrome.js). The result was the screenshot: the workspace's listing with no chrome at
//     all and no way out but a reload. The two rules must agree: where the box is not drawn, the
//     workspace is not drawn either. The session is untouched (see below), so stepping back out of
//     settings finds the workspace exactly as it was left, box and all.
//
// What this rule deliberately does NOT do is end the session. The session holds the user's picks,
// the armed suggestion and the archive receipts, and reading one thread is not a decision to throw
// any of that away — so the rail and the sidebar go on showing the session, and navigating back to
// the workspace finds it exactly as it was left. Ownership of one column is not lifecycle.
//
// Pure, and separate from the component, so "does the thread beat the session" is answerable
// without mounting the shell.
const SESSION_EXEMPT = [
  (path) => path.startsWith('/emails/workspace'),
  (path) => /^\/emails\/timeline\/[^/]+/.test(path),
  // busy-my-web#562 — no box on settings, so no workspace on settings (see the note above).
  (path) => path.startsWith('/settings'),
  // Pouria, 2026-09-29: "when we turn on the email workspace then move to email module i can still
  // see the email workspace". A module page (/emails, /tasks, /events, /notes, /contacts, /files —
  // listing or detail) is a place the user CHOSE to go to, so it draws itself. The session is left
  // alone, as above: back on Home, the workspace is exactly where it was left.
  (path) => MODULE_PREFIXES.some(prefix => path === prefix || path.startsWith(`${prefix}/`)),
]

export function emailWorkspaceOwnsMiddle({ path, sessionActive, hasGroups } = {}) {
  const p = String(path || '')
  if (SESSION_EXEMPT.some(exempt => exempt(p))) return false
  return !!sessionActive || !!hasGroups
}
